import React, { useState, useRef, useCallback } from 'react';
import {
  Upload, ShieldCheck, Loader2, AlertTriangle, CheckCircle, FileCode,
  KeyRound, Copy, ScanLine,
} from 'lucide-react';
import { Button } from '../index';
import { uploadWasmSkillBlob } from '../../../services/api';

/* Step 3 (Platform-Eng workflow) — pill style for the cosign
 * verification status the server returns after upload. Aligned with
 * Skill.COSIGN_STATUS_* discriminator values from the backend. */
const COSIGN_PILL = {
  unverified:     { label: 'unverified',        bg: 'bg-v2-warning-soft', text: 'text-v2-warning-strong', border: 'border-v2-warning/30' },
  'stub-skipped': { label: 'stub-skipped',      bg: 'bg-v2-action-soft',  text: 'text-v2-action-strong',  border: 'border-v2-action/30' },
  verified:       { label: 'cosign verified',   bg: 'bg-v2-success-soft', text: 'text-v2-success-strong', border: 'border-v2-success/30' },
  failed:         { label: 'verification failed', bg: 'bg-v2-danger-soft',  text: 'text-v2-danger-strong',  border: 'border-v2-danger/30' },
};

/**
 * P0-2 — WasmSkillUploader
 *
 * <p>Drop-in component for the skill creation / edit flow. Accepts a
 * .wasm file and three tuning knobs (entry function, memory cap,
 * timeout) and POSTs to the backend's /wasm-blob endpoint.</p>
 *
 * <p>The uploader does NOT create the Skill row — it expects an
 * existing wasm_skill to attach the binary to. Create the Skill via
 * the normal skill-create flow with skillType='wasm_skill' first,
 * then pass its id here.</p>
 *
 * <h3>Props</h3>
 * <ul>
 *   <li><code>agentId</code> — required UUID of the owning agent.</li>
 *   <li><code>skillId</code> — required UUID of the wasm_skill row.</li>
 *   <li><code>currentBlob</code> — optional. When the skill already
 *       has a wasmBlob the UI shows a "replace" affordance instead of
 *       a fresh upload prompt.</li>
 *   <li><code>onUploaded</code> — optional callback fired with the
 *       updated Skill row after a successful upload.</li>
 *   <li><code>defaults</code> — optional {entryFunction, maxMemoryMb,
 *       maxExecutionMs} to pre-fill the tuning inputs.</li>
 * </ul>
 */
export function WasmSkillUploader({
  agentId,
  skillId,
  currentBlob = null,
  onUploaded,
  defaults = {},
}) {
  const [file, setFile] = useState(null);
  const [entryFunction, setEntryFunction] = useState(
    defaults.entryFunction || 'execute');
  const [maxMemoryMb, setMaxMemoryMb] = useState(
    defaults.maxMemoryMb || 16);
  const [maxExecutionMs, setMaxExecutionMs] = useState(
    defaults.maxExecutionMs || 5000);
  /* Step 3 — multi-export input as a CSV string in the UI; we
   * parse on submit. Pre-fills with the legacy entryFunction so
   * the form is self-documenting ("by default we ship one export
   * named 'execute'"). */
  const [entryFunctionsCsv, setEntryFunctionsCsv] = useState(
    defaults.entryFunctions
      ? (Array.isArray(defaults.entryFunctions)
          ? defaults.entryFunctions.join(', ')
          : String(defaults.entryFunctions))
      : (defaults.entryFunction || 'execute'));
  /* Step 3 — supply-chain provenance fields. All optional but the
   * upload UI surfaces them prominently because the Step 3 walkthrough
   * specifically flags "accepting unsigned wasm for a control-plane
   * skill is unacceptable for production." */
  const [artifactUrl, setArtifactUrl] = useState(defaults.artifactUrl || '');
  const [signatureUrl, setSignatureUrl] = useState(defaults.signatureUrl || '');
  const [expectedSpiffeIssuer, setExpectedSpiffeIssuer] = useState(
    defaults.expectedSpiffeIssuer || '');
  /* Step 3 — server-computed digest + cosign result, surfaced after
   * a successful upload. We render the digest with a Copy button so
   * the operator can paste it into the Rego policy's
   * module_digest_at_register predicate. */
  const [uploadResult, setUploadResult] = useState(null);
  const [copiedDigest, setCopiedDigest] = useState(false);
  const [state, setState] = useState('idle'); // idle | uploading | done | error
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const onPickFile = (e) => {
    const f = e.target?.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.wasm')) {
      setError(`Selected file is "${f.name}" — expected a .wasm module.`);
      setFile(null);
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setError(`Module is ${(f.size / 1024 / 1024).toFixed(2)} MiB; cap is 8 MiB.`);
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
  };

  const onUpload = useCallback(async () => {
    if (!file) {
      setError('Pick a .wasm file first');
      return;
    }
    setState('uploading');
    setError(null);
    /* Step 3 — split the CSV into a clean array; trim and filter
     * empties so trailing commas / whitespace don't break the
     * backend's wasm-export-name validation. */
    const parsedEntryFunctions = entryFunctionsCsv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const r = await uploadWasmSkillBlob(agentId, skillId, file, {
      // Legacy single-export still sent so an old backend would
      // accept the call too. New backend prefers entryFunctions when
      // both are present.
      entryFunction: parsedEntryFunctions[0] || entryFunction,
      entryFunctions: parsedEntryFunctions,
      maxMemoryMb, maxExecutionMs,
      artifactUrl:          artifactUrl.trim() || undefined,
      signatureUrl:         signatureUrl.trim() || undefined,
      expectedSpiffeIssuer: expectedSpiffeIssuer.trim() || undefined,
    });
    if (r?.ok === false) {
      setState('error');
      setError(r.error || 'Upload failed');
      return;
    }
    setState('done');
    setUploadResult(r);
    if (typeof onUploaded === 'function') onUploaded(r);
    // Reset the file picker so a second upload doesn't silently re-use.
    if (inputRef.current) inputRef.current.value = '';
    setFile(null);
  }, [agentId, skillId, file, entryFunction, entryFunctionsCsv,
      maxMemoryMb, maxExecutionMs, artifactUrl, signatureUrl,
      expectedSpiffeIssuer, onUploaded]);

  /* Step 3 — Copy the server-computed module digest to clipboard.
   * The operator pastes this into their Rego policy's
   * module_digest_at_register predicate. */
  const onCopyDigest = useCallback(async () => {
    const d = uploadResult?.wasmModuleDigest;
    if (!d) return;
    try {
      await navigator.clipboard.writeText(d);
      setCopiedDigest(true);
      setTimeout(() => setCopiedDigest(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  }, [uploadResult]);

  const hasExisting = !!currentBlob;

  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={14} className="text-v2-success-strong" />
        <h3 className="text-[13px] font-semibold text-v2-ink m-0">
          Sandboxed Wasm body
        </h3>
        <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold ml-1">
          Chicory runtime · WASI deny-all
        </span>
      </div>

      <p className="text-[12px] text-v2-ink-secondary m-0 mb-3">
        Upload the compiled <code className="font-mono-v2">.wasm</code> module that
        runs when this skill fires. The sandbox enforces hard memory + wall-clock
        caps, denies all host imports by default, and audits every invocation.
        See the developer guide for the host ABI and how to compile from Rust /
        Go / Java.
      </p>

      {hasExisting && (
        <div className="mb-3 px-3 py-2 rounded-v2-sm bg-v2-success-soft border border-v2-success/30 text-[11.5px] text-v2-success-strong flex items-center gap-2">
          <FileCode size={13} />
          A Wasm module is currently attached to this skill. Uploading a new
          one will replace it and invalidate the cached parse.
        </div>
      )}

      {/* Row 1 — module file + multi-export entry functions */}
      <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: '1.4fr 1.2fr' }}>
        <label className="block">
          <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            Module file (.wasm)
          </span>
          <input
            ref={inputRef}
            type="file"
            accept=".wasm,application/wasm"
            onChange={onPickFile}
            className="block w-full text-[12px] file:mr-3 file:py-1.5 file:px-3 file:rounded-v2-sm file:border file:border-v2-border file:bg-v2-surface-muted file:text-v2-ink hover:file:bg-v2-action-soft"
          />
          {file && (
            <p className="text-[11px] text-v2-ink-muted mt-1">
              Selected: <code className="font-mono-v2">{file.name}</code> ·{' '}
              {(file.size / 1024).toFixed(1)} KiB
            </p>
          )}
        </label>

        <label className="block">
          <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            Exported entry functions (comma-separated)
          </span>
          <input
            type="text"
            value={entryFunctionsCsv}
            onChange={(e) => setEntryFunctionsCsv(e.target.value)}
            placeholder="start, stop, status"
            className="block w-full text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm font-mono-v2"
          />
          <p className="text-[10.5px] text-v2-ink-muted mt-1">
            First name is the default export. Invocation surface picks which one to call.
          </p>
        </label>
      </div>

      {/* Row 2 — runtime limits */}
      <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <label className="block">
          <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            Memory (MiB)
          </span>
          <input
            type="number" min={1} max={256}
            value={maxMemoryMb}
            onChange={(e) => setMaxMemoryMb(Number(e.target.value))}
            className="block w-full text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm font-mono-v2"
          />
        </label>

        <label className="block">
          <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            Timeout (ms)
          </span>
          <input
            type="number" min={1} max={60000}
            value={maxExecutionMs}
            onChange={(e) => setMaxExecutionMs(Number(e.target.value))}
            className="block w-full text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm font-mono-v2"
          />
        </label>
      </div>

      {/* Step 3 — supply-chain provenance section. Visually distinct so
          operators see this is the trust boundary, not just another
          tuning knob. */}
      <div className="border border-v2-border-subtle rounded-v2-sm overflow-hidden mb-3">
        <div className="px-3 py-1.5 bg-v2-surface-muted/30 flex items-center gap-2">
          <KeyRound size={12} className="text-v2-ink-muted" />
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">
            Supply-chain provenance (cosign)
          </span>
          <span className="text-[10.5px] text-v2-ink-muted italic ml-auto">
            optional but recommended for control-plane skills
          </span>
        </div>
        <div className="p-3 grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <label className="block">
            <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
              Artifact URL
            </span>
            <input
              type="text"
              value={artifactUrl}
              onChange={(e) => setArtifactUrl(e.target.value)}
              placeholder="oci://ghcr.io/acme/wasm-fleet-controller@sha256:…"
              className="block w-full text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm font-mono-v2"
            />
          </label>
          <label className="block">
            <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
              Signature URL (.sig / bundle)
            </span>
            <input
              type="text"
              value={signatureUrl}
              onChange={(e) => setSignatureUrl(e.target.value)}
              placeholder="oci://ghcr.io/acme/wasm-fleet-controller:sha256-…sig"
              className="block w-full text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm font-mono-v2"
            />
          </label>
          <label className="block" style={{ gridColumn: 'span 2' }}>
            <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
              Expected SPIFFE issuer
            </span>
            <input
              type="text"
              value={expectedSpiffeIssuer}
              onChange={(e) => setExpectedSpiffeIssuer(e.target.value)}
              placeholder="spiffe://acme.example/ci/build-bot"
              className="block w-full text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm font-mono-v2"
            />
            <p className="text-[10.5px] text-v2-ink-muted mt-1">
              The SAN cosign must match for keyless verification. Real signature
              verification is currently stubbed; fields are persisted and surfaced
              in the OPA decision viewer and audit chain.
            </p>
          </label>
        </div>
      </div>

      {error && (
        <div className="mt-3 px-3 py-2 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/30 text-[11.5px] text-v2-warning-strong flex items-center gap-2">
          <AlertTriangle size={13} /> {error}
        </div>
      )}

      {state === 'done' && uploadResult && (
        <div className="mt-3 border border-v2-success/30 rounded-v2-sm overflow-hidden">
          <div className="px-3 py-2 bg-v2-success-soft text-[11.5px] text-v2-success-strong flex items-center gap-2 border-b border-v2-success/30">
            <CheckCircle size={13} /> Wasm module uploaded and cache invalidated.
            The next invocation will parse the new bytes.
          </div>
          {/* Step 3 — digest + cosign result. The digest is what the
              operator pastes into the Rego module_digest_at_register
              predicate. */}
          <div className="p-3 space-y-2 text-[11.5px] bg-v2-surface">
            <div className="flex items-baseline gap-3">
              <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold w-40 shrink-0">
                Module digest (SHA-256)
              </span>
              <code className="flex-1 font-mono-v2 break-all text-v2-ink">
                {uploadResult.wasmModuleDigest || '—'}
              </code>
              {uploadResult.wasmModuleDigest && (
                <button
                  type="button"
                  onClick={onCopyDigest}
                  title="Copy digest to clipboard"
                  className="inline-flex items-center gap-1 text-[10.5px] text-v2-ink-muted hover:text-v2-ink v2-focus-ring"
                >
                  {copiedDigest
                    ? <CheckCircle size={11} className="text-v2-success-strong" />
                    : <Copy size={11} />}
                  {copiedDigest ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>

            {Array.isArray(uploadResult.wasmEntryFunctions)
                && uploadResult.wasmEntryFunctions.length > 0 && (
              <div className="flex items-baseline gap-3">
                <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold w-40 shrink-0">
                  Declared exports
                </span>
                <code className="flex-1 font-mono-v2 text-v2-ink">
                  {uploadResult.wasmEntryFunctions.join(', ')}
                </code>
              </div>
            )}

            <div className="flex items-baseline gap-3">
              <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold w-40 shrink-0">
                Cosign verification
              </span>
              <span className="flex-1">
                {(() => {
                  const pill = COSIGN_PILL[uploadResult.cosignStatus] || COSIGN_PILL.unverified;
                  return (
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-v2-pill text-[10.5px] font-semibold border ${pill.bg} ${pill.text} ${pill.border}`}>
                      <ScanLine size={10} /> {pill.label}
                    </span>
                  );
                })()}
              </span>
            </div>
            {uploadResult.cosignReason && (
              <p className="text-[10.5px] text-v2-ink-muted italic ml-[10.5rem]">
                {uploadResult.cosignReason}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={!file || state === 'uploading'}
          onClick={onUpload}
          iconLeft={state === 'uploading'
            ? <Loader2 size={13} className="animate-spin" />
            : <Upload size={13} />}
        >
          {state === 'uploading'
            ? 'Uploading…'
            : hasExisting ? 'Replace module' : 'Upload module'}
        </Button>
      </div>
    </div>
  );
}

/**
 * P0-2 — Inline pill for skill lists indicating the row runs in the
 * Wasm sandbox. Mount next to the skill name in tables / cards.
 */
export function WasmSandboxBadge() {
  return (
    <span
      title="This skill body runs inside the Chicory WebAssembly sandbox with hard memory + wall-clock limits and no host imports."
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-v2-pill text-[10.5px] font-semibold border bg-v2-success-soft text-v2-success-strong border-v2-success/30"
    >
      <ShieldCheck size={10} />
      Sandboxed
    </span>
  );
}

export default WasmSkillUploader;
